import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "display", "dropdown", "hourScroll", "minuteScroll"];

  static values = {
    increment: { type: Number, default: 5 }
  };

  connect() {
    let normalized = this.normalize(this.inputTarget.value);

    // Если значение пустое, используем текущее время с округлением минут
    if (!normalized) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      // Округляем минуты до ближайших 5
      const roundedMinute = Math.round(currentMinute / 5) * 5;
      const finalMinute = roundedMinute >= 60 ? 0 : roundedMinute;
      normalized = `${this.pad(currentHour)}:${this.pad(finalMinute)}`;
    }

    const [hour, minute] = normalized.split(":");
    this.initialHour = parseInt(hour, 10);
    this.initialMinute = parseInt(minute, 10);

    this.scrollTimeout = null;
    this.hourScrollTimeout = null;
    this.minuteScrollTimeout = null;
    this.isOpen = false;

    requestAnimationFrame(() => {
      this.setInitialActiveStates();
      if (window.innerWidth <= 992) {
        // Ждем пока элементы получат размеры перед скролом
        this.waitForLayoutAndScroll();
      }
    });

    // Закрываем dropdown при клике вне его
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
  }

  togglePicker(event) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.dropdownTarget.hidden = false;
      requestAnimationFrame(() => {
        this.scrollToCurrentValues();
      });
    } else {
      this.dropdownTarget.hidden = true;
    }
  }

  handleInput(event) {
    let value = event.target.value;

    // Разрешаем только цифры и двоеточие
    value = value.replace(/[^\d:]/g, '');

    // Автоматически добавляем двоеточие после двух цифр
    if (value.length === 2 && !value.includes(':')) {
      value += ':';
    }

    event.target.value = value;
  }

  handleBlur(event) {
    const value = event.target.value.trim();

    // Проверяем формат HH:MM
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    const match = value.match(timeRegex);

    if (match) {
      const hour = parseInt(match[1], 10);
      const minute = parseInt(match[2], 10);

      // Округляем минуты до ближайшего значения с шагом 5
      const roundedMinute = Math.round(minute / this.incrementValue) * this.incrementValue;
      const finalMinute = roundedMinute >= 60 ? 0 : roundedMinute;

      const timeString = `${this.pad(hour)}:${this.pad(finalMinute)}`;

      // Обновляем оба поля
      this.displayTarget.value = timeString;
      this.inputTarget.value = timeString;

      // Обновляем активные состояния в dropdown
      this.updateActiveStatesFromValue(hour, finalMinute);
    } else if (value) {
      // Если формат неверный, возвращаем предыдущее значение
      this.displayTarget.value = this.inputTarget.value;
    }
  }

  updateActiveStatesFromValue(hour, minute) {
    // Снимаем все активные состояния
    this.hourScrollTarget.querySelectorAll('.meeting-modal__time-option').forEach(el => {
      el.classList.remove('active');
    });
    this.minuteScrollTarget.querySelectorAll('.meeting-modal__time-option').forEach(el => {
      el.classList.remove('active');
    });

    // Устанавливаем новые активные состояния
    const hourOption = this.hourScrollTarget.querySelector(`[data-value="${hour}"]`);
    if (hourOption) {
      hourOption.classList.add('active');
    }

    const minuteOption = this.minuteScrollTarget.querySelector(`[data-value="${minute}"]`);
    if (minuteOption) {
      minuteOption.classList.add('active');
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target) && this.isOpen) {
      this.isOpen = false;
      this.dropdownTarget.hidden = true;
    }
  }

  waitForLayoutAndScroll(attempts = 0) {
    const maxAttempts = 20; // Максимум 20 попыток (1 секунда)

    if (attempts >= maxAttempts) {
      this.scrollToCurrentValues();
      return;
    }

    const hourOption = this.hourScrollTarget.querySelector(`[data-value="${this.initialHour}"]`);
    const container = this.hourScrollTarget;

    if (hourOption && container.clientHeight > 0 && hourOption.offsetHeight > 0) {
      this.scrollToCurrentValues();
    } else {
      setTimeout(() => {
        this.waitForLayoutAndScroll(attempts + 1);
      }, 50);
    }
  }

  scrollToCurrentValues() {
    // На мобиле используем scrollTop напрямую из-за scroll-snap
    const hourOption = this.hourScrollTarget.querySelector(`[data-value="${this.initialHour}"]`);

    if (hourOption) {
      const container = this.hourScrollTarget;
      const containerStyle = window.getComputedStyle(container);
      const paddingTop = parseInt(containerStyle.paddingTop);
      const containerHeight = container.clientHeight;
      const optionTop = hourOption.offsetTop;
      const optionHeight = hourOption.offsetHeight;
      const scrollValue = optionTop - paddingTop - (containerHeight / 2) + (optionHeight / 2);
      container.scrollTop = scrollValue;
    }

    const minuteOption = this.minuteScrollTarget.querySelector(`[data-value="${this.initialMinute}"]`);

    if (minuteOption) {
      const container = this.minuteScrollTarget;
      const containerStyle = window.getComputedStyle(container);
      const paddingTop = parseInt(containerStyle.paddingTop);
      const containerHeight = container.clientHeight;
      const optionTop = minuteOption.offsetTop;
      const optionHeight = minuteOption.offsetHeight;
      const scrollValue = optionTop - paddingTop - (containerHeight / 2) + (optionHeight / 2);
      container.scrollTop = scrollValue;
    }
  }

  selectOption(event) {
    const option = event.target.closest('.meeting-modal__time-option');
    if (!option) return;

    event.stopPropagation();

    const container = option.closest('[data-time-spinner-target]');
    if (!container) return;

    const value = parseInt(option.dataset.value, 10);

    // Снимаем активное состояние со всех элементов контейнера
    container.querySelectorAll('.meeting-modal__time-option').forEach(el => {
      el.classList.remove('active');
    });

    // Устанавливаем активное состояние на выбранный элемент
    option.classList.add('active');

    // Обновляем значения
    this.updateTimeValue();
  }

  updateTimeValue() {
    const hour = this.getSelectedValue(this.hourScrollTarget);
    const minute = this.getSelectedValue(this.minuteScrollTarget);

    if (hour !== null && minute !== null) {
      const timeString = `${this.pad(hour)}:${this.pad(minute)}`;
      this.inputTarget.value = timeString;
      if (this.hasDisplayTarget) {
        this.displayTarget.value = timeString;
      }
    }
  }

  onHourScroll() {
    if (window.innerWidth <= 992) {
      clearTimeout(this.hourScrollTimeout);
      this.hourScrollTimeout = setTimeout(() => {
        this.updateActiveFromScroll(this.hourScrollTarget);
      }, 150);
    }
  }

  onMinuteScroll() {
    if (window.innerWidth <= 992) {
      clearTimeout(this.minuteScrollTimeout);
      this.minuteScrollTimeout = setTimeout(() => {
        this.updateActiveFromScroll(this.minuteScrollTarget);
      }, 150);
    }
  }

  updateActiveFromScroll(container) {
    const options = Array.from(container.querySelectorAll('.meeting-modal__time-option'));
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.top + containerRect.height / 2;

    let closestOption = null;
    let closestDistance = Infinity;

    options.forEach(option => {
      const optionRect = option.getBoundingClientRect();
      const optionCenter = optionRect.top + optionRect.height / 2;
      const distance = Math.abs(containerCenter - optionCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestOption = option;
      }
    });

    if (closestOption) {
      options.forEach(opt => opt.classList.remove('active'));
      closestOption.classList.add('active');
      this.updateTimeValue();
    }
  }

  getSelectedValue(container) {
    if (!container) return null;

    const activeOption = container.querySelector('.meeting-modal__time-option.active');
    if (activeOption) {
      return parseInt(activeOption.dataset.value, 10);
    }

    return null;
  }

  setInitialActiveStates() {
    // Устанавливаем активное состояние для начальных значений
    const hourOption = this.hourScrollTarget.querySelector(`[data-value="${this.initialHour}"]`);
    if (hourOption) {
      hourOption.classList.add('active');
    }

    const minuteOption = this.minuteScrollTarget.querySelector(`[data-value="${this.initialMinute}"]`);
    if (minuteOption) {
      minuteOption.classList.add('active');
    }

    // Обновляем оба поля
    const timeString = `${this.pad(this.initialHour)}:${this.pad(this.initialMinute)}`;
    this.inputTarget.value = timeString;
    if (this.hasDisplayTarget) {
      this.displayTarget.value = timeString;
    }
  }

  normalize(value) {
    if (!value) return null;
    const parts = this.split(value);
    const { hour, minute } = this.snapMinutes(parts.hour, parts.minute);
    return `${this.pad(hour)}:${this.pad(minute)}`;
  }

  split(value) {
    const [hourPart = "0", minutePart = "0"] = value.split(":");
    const hour = parseInt(hourPart, 10);
    const minute = parseInt(minutePart, 10);
    return {
      hour: Number.isNaN(hour) ? 0 : hour,
      minute: Number.isNaN(minute) ? 0 : minute
    };
  }

  snapMinutes(hour, minute) {
    const safeHour = this.clamp(hour, 0, 23);
    const safeMinute = this.clamp(minute, 0, 59);
    const increment = this.incrementValue;
    const rounded = Math.round(safeMinute / increment) * increment;

    if (rounded >= 60) {
      return { hour: (safeHour + 1) % 24, minute: 0 };
    }

    return { hour: safeHour, minute: rounded };
  }

  clamp(value, min, max) {
    if (Number.isNaN(value)) return min;
    return Math.min(max, Math.max(min, value));
  }

  pad(value) {
    return String(value).padStart(2, "0");
  }
}
